import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingStorefrontIcon,
  UserIcon,
  TruckIcon,
  BriefcaseIcon,
  UsersIcon,
  XMarkIcon,
  CheckCircleIcon,
  ReceiptPercentIcon,
  ClockIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'
import { customerService } from '../../services/customerService'
import { groupService } from '../../services/groupService'
import toast from 'react-hot-toast'

interface GroupFormData {
  group_name: string
  group_code: string
  price_tier: string
  payment_terms_days: number
  credit_limit: number
  discount_percent: number
}

const getGroupIcon = (tier: string) => {
  switch (tier) {
    case 'wholesale': return BuildingStorefrontIcon
    case 'corporate': return BriefcaseIcon
    case 'consignment': return TruckIcon
    default: return UserIcon
  }
}

const getGroupColor = (tier: string) => {
  switch (tier) {
    case 'wholesale': return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'corporate': return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'consignment': return 'bg-orange-100 text-orange-800 border-orange-200'
    default: return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const priceTiers = [
  { value: 'retail', label: 'Retail', description: 'Standard retail pricing' },
  { value: 'wholesale', label: 'Wholesale', description: 'Discounted pricing for bulk buyers' },
  { value: 'corporate', label: 'Corporate', description: 'Special pricing for companies' },
  { value: 'consignment', label: 'Consignment', description: 'Pay after sale arrangement' },
]

export const CustomerGroupsPage = () => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<any>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<any>(null)

  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => customerService.getGroups(),
  })

  const createMutation = useMutation({
    mutationFn: (data: GroupFormData) => groupService.create(data),
    onSuccess: () => {
      toast.success('Customer group created successfully')
      queryClient.invalidateQueries({ queryKey: ['customer-groups'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create group')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: GroupFormData }) => groupService.update(id, data),
    onSuccess: () => {
      toast.success('Customer group updated successfully')
      queryClient.invalidateQueries({ queryKey: ['customer-groups'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update group')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => groupService.delete(id),
    onSuccess: () => {
      toast.success('Customer group deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['customer-groups'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setShowDeleteModal(false)
      setGroupToDelete(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete group')
    },
  })

  const groups = groupsData?.data?.data || []

  const closeModal = () => {
    setShowModal(false)
    setEditingGroup(null)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data: GroupFormData = {
      group_name: formData.get('group_name') as string,
      group_code: formData.get('group_code') as string,
      price_tier: formData.get('price_tier') as string,
      payment_terms_days: parseInt(formData.get('payment_terms_days') as string) || 0,
      credit_limit: parseFloat(formData.get('credit_limit') as string) || 0,
      discount_percent: parseFloat(formData.get('discount_percent') as string) || 0,
    }

    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.group_id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (group: any) => {
    setEditingGroup(group)
    setShowModal(true)
  }

  const handleDeleteClick = (group: any) => {
    setGroupToDelete(group)
    setShowDeleteModal(true)
  }

  const handleDelete = () => {
    if (groupToDelete) {
      deleteMutation.mutate(groupToDelete.group_id)
    }
  }

  const generateGroupCode = (name: string) => {
    return name.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/customers"
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Groups</h1>
            <p className="text-sm text-gray-500">
              Manage customer clusters and their pricing/payment terms
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-5 h-5" />
          Create Group
        </button>
      </div>

      {/* Groups Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No customer groups</h3>
          <p className="text-gray-500 mb-4">
            Create groups to organize customers by pricing tier and payment terms
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            Create First Group
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group: any) => {
            const Icon = getGroupIcon(group.price_tier)
            const colorClass = getGroupColor(group.price_tier)
            const tierLabel = priceTiers.find(t => t.value === group.price_tier)?.label || group.price_tier

            return (
              <div
                key={group.group_id}
                className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Header */}
                <div className={`p-4 border-b ${colorClass}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white bg-opacity-50 rounded-lg">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{group.group_name}</h3>
                        <p className="text-sm opacity-75">{group.group_code}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(group)}
                        className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded-lg transition-colors"
                        title="Edit group"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(group)}
                        className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded-lg transition-colors"
                        title="Delete group"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 space-y-4">
                  {/* Tier Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Pricing Tier:</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-sm font-medium rounded">
                      {tierLabel}
                    </span>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {group.payment_terms_days > 0 && (
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Payment Terms</p>
                          <p className="font-medium text-sm">Net {group.payment_terms_days} days</p>
                        </div>
                      </div>
                    )}
                    
                    {group.discount_percent > 0 && (
                      <div className="flex items-center gap-2">
                        <ReceiptPercentIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Discount</p>
                          <p className="font-medium text-sm">{group.discount_percent}%</p>
                        </div>
                      </div>
                    )}
                    
                    {group.credit_limit > 0 && (
                      <div className="flex items-center gap-2">
                        <CreditCardIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Credit Limit</p>
                          <p className="font-medium text-sm">KSh {Number(group.credit_limit).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Customers</p>
                        <p className="font-medium text-sm">{group._count?.customers || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* View Customers Link */}
                  <Link
                    to={`/customers?group=${group.group_id}`}
                    className="block w-full text-center py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    View {group._count?.customers || 0} Customers →
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closeModal}
            />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <div className="bg-primary-600 px-4 py-4 sm:px-6 flex justify-between items-center">
                <h3 className="text-lg font-medium text-white">
                  {editingGroup ? 'Edit Customer Group' : 'Create Customer Group'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-primary-100 hover:text-white"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
                <div className="space-y-4">
                  {/* Group Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Name *
                    </label>
                    <input
                      type="text"
                      name="group_name"
                      defaultValue={editingGroup?.group_name || ''}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. Premium Wholesale"
                    />
                  </div>

                  {/* Group Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Code *
                    </label>
                    <input
                      type="text"
                      name="group_code"
                      defaultValue={editingGroup?.group_code || ''}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 uppercase"
                      placeholder="e.g. PREMIUM_WS"
                      onChange={(e) => {
                        if (!editingGroup) {
                          const name = (e.target.form?.group_name as HTMLInputElement)?.value || ''
                          if (name && !e.target.value) {
                            e.target.value = generateGroupCode(name)
                          }
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Unique identifier for this group (auto-generated from name)
                    </p>
                  </div>

                  {/* Price Tier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Tier *
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {priceTiers.map((tier) => (
                        <label
                          key={tier.value}
                          className={`cursor-pointer border-2 rounded-lg p-3 transition-colors ${
                            editingGroup?.price_tier === tier.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="price_tier"
                            value={tier.value}
                            defaultChecked={editingGroup?.price_tier === tier.value}
                            required
                            className="sr-only"
                          />
                          <p className="font-medium text-sm">{tier.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{tier.description}</p>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms (Days)
                    </label>
                    <select
                      name="payment_terms_days"
                      defaultValue={editingGroup?.payment_terms_days || '0'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="0">Immediate Payment (0 days)</option>
                      <option value="7">Net 7 days</option>
                      <option value="14">Net 14 days</option>
                      <option value="15">Net 15 days</option>
                      <option value="30">Net 30 days</option>
                      <option value="45">Net 45 days</option>
                      <option value="60">Net 60 days</option>
                      <option value="90">Net 90 days</option>
                    </select>
                  </div>

                  {/* Credit Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Credit Limit (KSh)
                    </label>
                    <input
                      type="number"
                      name="credit_limit"
                      defaultValue={editingGroup?.credit_limit || ''}
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum credit amount for customers in this group
                    </p>
                  </div>

                  {/* Discount Percent */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Discount (%)
                    </label>
                    <input
                      type="number"
                      name="discount_percent"
                      defaultValue={editingGroup?.discount_percent || ''}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Automatic discount applied to all sales for this group
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        {editingGroup ? 'Update Group' : 'Create Group'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && groupToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Customer Group</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{groupToDelete.group_name}</strong>?
            </p>
            
            {groupToDelete._count?.customers > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ This group has {groupToDelete._count.customers} customer(s). 
                  They will be moved to the default Retail group.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
